import React, { useState, useEffect } from 'react';
import { veliKaydet, veliListele, veliSil, veliGuncelle } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { HiUserGroup, HiSearch, HiPencil, HiTrash, HiPlus, HiX, HiPhotograph, HiCheckCircle, HiInformationCircle } from 'react-icons/hi';
import { MdSchool } from 'react-icons/md';
import toast, { Toaster } from 'react-hot-toast';
import './AdminPanelPage.css';

const AdminPanelPage = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    cocukAdi: '',
    veliAdi: '',
    ogretmenEmail: '',
    fotografBase64: ''
  });
  const [loading, setLoading] = useState(false);
  const [veliler, setVeliler] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingVeli, setEditingVeli] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        toast.error('Dosya boyutu 5MB\'dan küçük olmalı');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          fotografBase64: reader.result.split(',')[1]
        });
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    loadVeliler();
  }, []);

  const loadVeliler = async () => {
    setLoadingList(true);
    try {
      const response = await veliListele(user?.role === 'super_admin' ? null : user?.okulId);
      if (response.success) {
        setVeliler(response.veliler || []);
      }
    } catch (err) {
      toast.error('Veli listesi alınamadı');
    } finally {
      setLoadingList(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await veliKaydet(formData, user?.okulId, user?.collectionId);

      if (response.success) {
        toast.success('Veli başarıyla kaydedildi!');
        setFormData({
          cocukAdi: '',
          veliAdi: '',
          ogretmenEmail: '',
          fotografBase64: ''
        });
        setImagePreview(null);
        document.getElementById('foto-input').value = '';
        loadVeliler();
      } else {
        toast.error(response.mesaj || 'Kayıt başarısız');
      }
    } catch (err) {
      toast.error(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (veliId, veliAdi) => {
    if (!window.confirm(`${veliAdi} kaydını silmek istediğinize emin misiniz?`)) {
      return;
    }

    const loadingToast = toast.loading('Siliniyor...');
    try {
      const response = await veliSil(veliId);
      if (response.success) {
        toast.success('Veli silindi', { id: loadingToast });
        loadVeliler();
      } else {
        toast.error(response.mesaj || 'Silme başarısız', { id: loadingToast });
      }
    } catch (err) {
      toast.error(err.message || 'Bir hata oluştu', { id: loadingToast });
    }
  };

  const handleEdit = (veli) => {
    setEditingVeli({
      ...veli,
      fotografBase64: ''
    });
    setEditImagePreview(null);
    setShowEditModal(true);
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        toast.error('Dosya boyutu 5MB\'dan küçük olmalı');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingVeli({
          ...editingVeli,
          fotografBase64: reader.result.split(',')[1]
        });
        setEditImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const loadingToast = toast.loading('Güncelleniyor...');
    try {
      const response = await veliGuncelle(editingVeli);
      if (response.success) {
        toast.success('Veli başarıyla güncellendi!', { id: loadingToast });
        setShowEditModal(false);
        setEditingVeli(null);
        setEditImagePreview(null);
        loadVeliler();
      } else {
        toast.error(response.mesaj || 'Güncelleme başarısız', { id: loadingToast });
      }
    } catch (err) {
      toast.error(err.message || 'Bir hata oluştu', { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const filteredVeliler = veliler
    .filter(veli =>
      veli.veliAdi.toLowerCase().includes(searchTerm.toLowerCase()) ||
      veli.cocukAdi.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.veliAdi.localeCompare(b.veliAdi, 'tr'));

  return (
    <div className="admin-panel">
      <Toaster position="top-right" />

      {/* Header with Stats */}
      <div className="panel-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="header-title">
              <HiUserGroup className="header-icon" />
              Veli Yönetimi
            </h1>
            <p className="header-subtitle">
              {user?.role === 'super_admin'
                ? 'Tüm okulların veli kayıtlarını görüntüleyin ve yönetin'
                : 'Okula kayıtlı çocukların velilerini sisteme ekleyin'}
            </p>
            {user?.okulAdi && (
              <div className="school-badge">
                <MdSchool />
                <span>{user.okulAdi}</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-primary">
              <HiUserGroup />
            </div>
            <div className="stat-content">
              <p className="stat-label">Toplam Veli</p>
              <p className="stat-value">{veliler.length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-success">
              <HiCheckCircle />
            </div>
            <div className="stat-content">
              <p className="stat-label">Bu Ay Eklenen</p>
              <p className="stat-value">
                {veliler.filter(v => {
                  const kayitTarihi = new Date(v.kayitTarihi);
                  const now = new Date();
                  return kayitTarihi.getMonth() === now.getMonth() &&
                         kayitTarihi.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Form */}
      <div className="content-card animate-slideUp">
        <div className="card-header">
          <h2 className="card-title">
            <HiPlus />
            Yeni Veli Ekle
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="modern-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Çocuğun Adı Soyadı *</label>
              <input
                type="text"
                className="form-input"
                value={formData.cocukAdi}
                onChange={(e) => setFormData({ ...formData, cocukAdi: e.target.value })}
                placeholder="Örn: Ahmet Yılmaz"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Veli Adı Soyadı *</label>
              <input
                type="text"
                className="form-input"
                value={formData.veliAdi}
                onChange={(e) => setFormData({ ...formData, veliAdi: e.target.value })}
                placeholder="Örn: Ayşe Yılmaz"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Öğretmen / Yetkili E-posta *</label>
            <input
              type="email"
              className="form-input"
              value={formData.ogretmenEmail}
              onChange={(e) => setFormData({ ...formData, ogretmenEmail: e.target.value })}
              placeholder="ornek@okul.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Veli Fotoğrafı *</label>
            <div className="file-upload-wrapper">
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <button
                    type="button"
                    className="remove-image"
                    onClick={() => {
                      setImagePreview(null);
                      setFormData({ ...formData, fotografBase64: '' });
                      document.getElementById('foto-input').value = '';
                    }}
                  >
                    <HiX />
                  </button>
                </div>
              ) : (
                <label htmlFor="foto-input" className="file-upload-label">
                  <HiPhotograph className="upload-icon" />
                  <span>Fotoğraf Yükle</span>
                  <small>PNG, JPG (max 5MB)</small>
                </label>
              )}
              <input
                id="foto-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                required
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <div className="spinner-sm"></div>
                <span>Kaydediliyor...</span>
              </>
            ) : (
              <>
                <HiCheckCircle />
                <span>Kaydet</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Veli List */}
      <div className="content-card animate-slideUp">
        <div className="card-header">
          <h2 className="card-title">
            Kayıtlı Veliler ({filteredVeliler.length})
          </h2>
          {veliler.length > 0 && (
            <div className="search-wrapper">
              <HiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Veli veya çocuk adı ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
        </div>

        {loadingList ? (
          <div className="empty-state">
            <div className="spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        ) : filteredVeliler.length === 0 ? (
          <div className="empty-state">
            <HiUserGroup className="empty-icon" />
            <h3>Henüz kayıtlı veli yok</h3>
            <p>Yukarıdaki formu kullanarak ilk veliyi ekleyin</p>
          </div>
        ) : (
          <div className="veli-grid">
            {filteredVeliler.map((veli) => (
              <div key={veli.veliId} className="veli-card">
                <div className="veli-card-header">
                  <div className="veli-avatar">
                    {veli.veliAdi.charAt(0).toUpperCase()}
                  </div>
                  <div className="veli-info">
                    <h3 className="veli-name">{veli.veliAdi}</h3>
                    <p className="veli-child">{veli.cocukAdi}</p>
                  </div>
                </div>
                <div className="veli-card-body">
                  <div className="info-row">
                    <span className="info-label">Öğretmen:</span>
                    <span className="info-value">{veli.ogretmenEmail}</span>
                  </div>
                  {veli.kayitTarihi && (
                    <div className="info-row">
                      <span className="info-label">Kayıt:</span>
                      <span className="info-value">{new Date(veli.kayitTarihi).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                </div>
                <div className="veli-card-actions">
                  <button onClick={() => handleEdit(veli)} className="btn btn-sm btn-secondary">
                    <HiPencil /> Düzenle
                  </button>
                  <button onClick={() => handleDelete(veli.veliId, veli.veliAdi)} className="btn btn-sm btn-danger">
                    <HiTrash /> Sil
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="info-card">
        <HiInformationCircle className="info-card-icon" />
        <div className="info-card-content">
          <h3 className="info-card-title">Bilgilendirme</h3>
          <ul className="info-list">
            <li>Fotoğraflarınız yüksek güvenlik standartlarıyla saklanır</li>
            <li>Yüz bilgileri yapay zeka sistemiyle analiz edilir</li>
            <li>Sisteme eklenen veliler kapıda otomatik tanınacaktır</li>
            <li>Yüz tanıma sonrası öğretmene e-posta bildirimi gönderilir</li>
          </ul>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingVeli && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content animate-scaleIn" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <HiPencil />
                Veli Bilgilerini Düzenle
              </h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <HiX />
              </button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="modern-form">
              <div className="form-group">
                <label className="form-label">Çocuğun Adı Soyadı *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingVeli.cocukAdi}
                  onChange={(e) => setEditingVeli({ ...editingVeli, cocukAdi: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Veli Adı Soyadı *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingVeli.veliAdi}
                  onChange={(e) => setEditingVeli({ ...editingVeli, veliAdi: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Öğretmen / Yetkili E-posta *</label>
                <input
                  type="email"
                  className="form-input"
                  value={editingVeli.ogretmenEmail}
                  onChange={(e) => setEditingVeli({ ...editingVeli, ogretmenEmail: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Yeni Fotoğraf (opsiyonel)</label>
                <div className="file-upload-wrapper">
                  {editImagePreview ? (
                    <div className="image-preview">
                      <img src={editImagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => {
                          setEditImagePreview(null);
                          setEditingVeli({ ...editingVeli, fotografBase64: '' });
                        }}
                      >
                        <HiX />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="edit-foto-input" className="file-upload-label">
                      <HiPhotograph className="upload-icon" />
                      <span>Yeni Fotoğraf Yükle</span>
                      <small>Boş bırakırsanız mevcut fotoğraf korunur</small>
                    </label>
                  )}
                  <input
                    id="edit-foto-input"
                    type="file"
                    accept="image/*"
                    onChange={handleEditFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner-sm"></div>
                      <span>Güncelleniyor...</span>
                    </>
                  ) : (
                    <>
                      <HiCheckCircle />
                      <span>Güncelle</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelPage;
